import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

const Fish = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.15, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
        <capsuleGeometry args={[0.06, 0.1, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FF6347' : '#b04030'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Stripes */}
      {[-0.03, 0, 0.03].map((z, i) => (
        <mesh key={i} position={[0, 0.15, z]}>
          <boxGeometry args={[0.13, 0.02, 0.02]} />
          <meshStandardMaterial color={world === 'real' ? '#FFFFFF' : '#e0e0e8'} />
        </mesh>
      ))}
      {/* Tail fin */}
      <mesh position={[0, 0.15, -0.12]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.06, 0.08, 3]} />
        <meshStandardMaterial color={world === 'real' ? '#FF6347' : '#b04030'} />
      </mesh>
      {/* Dorsal fin */}
      <mesh position={[0, 0.22, 0]} rotation={[0, 0, 0]}>
        <coneGeometry args={[0.04, 0.06, 3]} />
        <meshStandardMaterial color={world === 'real' ? '#FF6347' : '#b04030'} />
      </mesh>
      {/* Eye */}
      <mesh position={[0.05, 0.16, 0.06]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
    </group>
  );
};

const Turtle = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Shell */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <sphereGeometry args={[0.15, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#228B22' : '#1a5a2a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Shell pattern */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i * Math.PI * 2) / 6;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.08, 0.12, Math.sin(angle) * 0.08]}>
            <circleGeometry args={[0.03, 6]} />
            <meshStandardMaterial color={world === 'real' ? '#2E8B57' : '#1a6a3a'} />
          </mesh>
        );
      })}
      {/* Head */}
      <mesh position={[0, 0.08, 0.18]} castShadow>
        <sphereGeometry args={[0.05, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#6B8E23' : '#4a6a2a'} />
      </mesh>
      {/* Eyes */}
      {[-0.02, 0.02].map((x, i) => (
        <mesh key={i} position={[x, 0.1, 0.21]}>
          <sphereGeometry args={[0.01, 6, 6]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
      {/* Flippers */}
      {[[-0.12, 0.1], [0.12, 0.1], [-0.1, -0.1], [0.1, -0.1]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.04, z]} rotation={[0, x > 0 ? -0.3 : 0.3, x > 0 ? 0.5 : -0.5]}>
          <capsuleGeometry args={[0.02, 0.06, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#6B8E23' : '#4a6a2a'} />
        </mesh>
      ))}
    </group>
  );
};

const Jellyfish = ({ world, hovered }: NPCModelProps) => {
  return (
    <group>
      {/* Bell/dome */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFB6C1' : '#DDA0DD'}
          emissive={world === 'spectral' ? '#DDA0DD' : '#FFB6C1'}
          emissiveIntensity={0.4}
          transparent
          opacity={0.7}
        />
      </mesh>
      {/* Inner glow */}
      <mesh position={[0, 0.18, 0]}>
        <sphereGeometry args={[0.08, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshBasicMaterial color={world === 'real' ? '#FF69B4' : '#BA55D3'} transparent opacity={0.5} />
      </mesh>
      {/* Tentacles */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i * Math.PI * 2) / 8;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.08, 0.05, Math.sin(angle) * 0.08]} rotation={[Math.sin(i) * 0.2, 0, Math.cos(i) * 0.2]}>
            <cylinderGeometry args={[0.008, 0.003, 0.2 + Math.sin(i * 2) * 0.05, 4]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFB6C1' : '#DDA0DD'}
              emissive={world === 'spectral' ? '#DDA0DD' : undefined}
              emissiveIntensity={0.3}
              transparent
              opacity={0.6}
            />
          </mesh>
        );
      })}
      {/* Glow light */}
      <pointLight
        position={[0, 0.15, 0]}
        color={world === 'real' ? '#FFB6C1' : '#DDA0DD'}
        intensity={0.5}
        distance={1}
      />
    </group>
  );
};

const Dolphin = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group rotation={[0, -0.5, 0]}>
      {/* Body */}
      <mesh position={[0, 0.15, 0]} rotation={[0, 0, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.25, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#708090' : '#506070'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.15, 0.22]} rotation={[0, 0, Math.PI / 2]}>
        <coneGeometry args={[0.04, 0.12, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} />
      </mesh>
      {/* Dorsal fin */}
      <mesh position={[0, 0.26, 0]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.05, 0.1, 3]} />
        <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} />
      </mesh>
      {/* Tail flukes */}
      <mesh position={[0, 0.13, -0.2]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.15, 0.08]} />
        <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} side={THREE.DoubleSide} />
      </mesh>
      {/* Pectoral fins */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.1, 0.05]} rotation={[0, 0, x > 0 ? 0.5 : -0.5]}>
          <planeGeometry args={[0.08, 0.04]} />
          <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* Eye */}
      <mesh position={[0.06, 0.17, 0.15]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
      {/* Belly */}
      <mesh position={[0, 0.1, 0.05]}>
        <capsuleGeometry args={[0.05, 0.15, 6, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#B8C8D8' : '#8898A8'} />
      </mesh>
    </group>
  );
};

const Crab = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.06, 0]} castShadow>
        <boxGeometry args={[0.15, 0.06, 0.12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FF4500' : '#b03020'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Claws */}
      {[-0.12, 0.12].map((x, i) => (
        <group key={i} position={[x, 0.06, 0.05]}>
          {/* Arm */}
          <mesh rotation={[0, 0, x > 0 ? 0.3 : -0.3]}>
            <boxGeometry args={[0.06, 0.03, 0.03]} />
            <meshStandardMaterial color={world === 'real' ? '#FF4500' : '#b03020'} />
          </mesh>
          {/* Pincer */}
          <mesh position={[x > 0 ? 0.05 : -0.05, 0.02, 0]}>
            <boxGeometry args={[0.04, 0.04, 0.025]} />
            <meshStandardMaterial color={world === 'real' ? '#FF6347' : '#c04030'} />
          </mesh>
        </group>
      ))}
      {/* Legs */}
      {[-1, 1].map((side) =>
        [0, 1, 2].map((i) => (
          <mesh key={`${side}-${i}`} position={[side * 0.08, 0.03, 0.03 - i * 0.04]} rotation={[0, 0, side * 0.8]}>
            <cylinderGeometry args={[0.01, 0.008, 0.08, 4]} />
            <meshStandardMaterial color={world === 'real' ? '#FF4500' : '#b03020'} />
          </mesh>
        ))
      )}
      {/* Eyes on stalks */}
      {[-0.04, 0.04].map((x, i) => (
        <group key={i} position={[x, 0.1, 0.05]}>
          <mesh>
            <cylinderGeometry args={[0.008, 0.008, 0.04, 4]} />
            <meshStandardMaterial color={world === 'real' ? '#FF4500' : '#b03020'} />
          </mesh>
          <mesh position={[0, 0.03, 0]}>
            <sphereGeometry args={[0.012, 6, 6]} />
            <meshBasicMaterial color="#000000" />
          </mesh>
        </group>
      ))}
    </group>
  );
};

export const oceanAnimals: NPCModelRegistry = {
  fish: Fish,
  turtle: Turtle,
  jellyfish: Jellyfish,
  dolphin: Dolphin,
  crab: Crab,
};
